
import Vue, { PropType } from "vue";
import {
  MobileBuildStatusResponse,
  MobileBuildStep,
  MobileBuildJob
} from "@/api";

// The component accepts the union of dispatch + status responses
type RunInfo = Partial<MobileBuildStatusResponse> & {
  // Fields that may come from the dispatch response
  runId?: number | null;
  workflow?: string;
  branch?: string;
  // Standard status fields
  id?: number;
  status: string;
  conclusion?: string | null;
  runUrl: string;
  createdAt?: string;
  jobs?: MobileBuildJob[];
};

export default Vue.extend({
  name: "BuildStatusPanel",
  props: {
    run: { type: Object as PropType<RunInfo>, required: true },
    polling: { type: Boolean, default: false },
    cancelling: { type: Boolean, default: false }
  },
  data: () => ({ now: Date.now(), tickTimer: null as number | null }),
  mounted() {
    // Tick once a second so the elapsed-time display stays live without
    // re-rendering the rest of the panel.
    this.tickTimer = window.setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    if (this.tickTimer !== null) window.clearInterval(this.tickTimer);
  },
  computed: {
    activeJob(): MobileBuildJob | null {
      const jobs = this.run.jobs || [];
      if (jobs.length === 0) return null;
      // Prefer the in-progress job, otherwise the last one (most relevant for
      // a completed run).
      return (
        jobs.find(j => j.status === "in_progress") ||
        jobs[jobs.length - 1] ||
        null
      );
    },
    steps(): MobileBuildStep[] {
      return this.activeJob ? this.activeJob.steps || [] : [];
    },
    progressPct(): number {
      const total = this.steps.length;
      if (total === 0) return this.run.status === "completed" ? 100 : 0;
      const done = this.steps.filter(s => s.status === "completed").length;
      return Math.round((done / total) * 100);
    },
    progressLabel(): string {
      if (this.steps.length === 0) {
        return this.run.status === "queued"
          ? "Waiting for runner…"
          : this.run.status === "completed"
          ? "Build finished"
          : "Build starting…";
      }
      const done = this.steps.filter(s => s.status === "completed").length;
      const inProgress = this.steps.find(s => s.status === "in_progress");
      const stepName = inProgress ? inProgress.name : "";
      if (this.run.status === "completed") {
        return `${done} / ${this.steps.length} steps`;
      }
      return inProgress
        ? `Step ${inProgress.number} of ${this.steps.length}: ${stepName}`
        : `${done} / ${this.steps.length} steps`;
    },
    progressColor(): string {
      if (this.run.status === "completed") {
        return this.run.conclusion === "success" ? "success" : "error";
      }
      return "primary";
    },
    isQueued(): boolean {
      return this.run.status === "queued" && this.steps.length === 0;
    },
    statusLabel(): string {
      if (this.run.status === "completed") {
        return `Completed: ${this.run.conclusion || "unknown"}`;
      }
      if (this.run.status === "in_progress") return "Running…";
      if (this.run.status === "queued") return "Queued";
      return this.run.status;
    },
    statusIcon(): string {
      if (this.run.status === "completed") {
        return this.run.conclusion === "success"
          ? "mdi-check-circle"
          : "mdi-alert-circle";
      }
      if (this.run.status === "in_progress") return "mdi-cog-outline";
      return "mdi-clock-outline";
    },
    statusColor(): string {
      if (this.run.status === "completed") {
        return this.run.conclusion === "success" ? "success" : "error";
      }
      return "primary";
    },
    canCancel(): boolean {
      return (
        this.run.status === "queued" || this.run.status === "in_progress"
      );
    },
    elapsed(): string {
      if (!this.run.createdAt) return "";
      const start = new Date(this.run.createdAt).getTime();
      const end =
        this.run.status === "completed" && this.run.updatedAt
          ? new Date(this.run.updatedAt).getTime()
          : this.now;
      const seconds = Math.max(0, Math.floor((end - start) / 1000));
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      if (m === 0) return `${s}s`;
      return `${m}m ${s.toString().padStart(2, "0")}s`;
    }
  },
  methods: {
    stepIcon(step: MobileBuildStep): string {
      if (step.status === "completed") {
        if (step.conclusion === "success") return "mdi-check-circle";
        if (step.conclusion === "skipped") return "mdi-minus-circle-outline";
        return "mdi-close-circle";
      }
      if (step.status === "in_progress") return "mdi-loading mdi-spin";
      return "mdi-circle-outline";
    },
    stepIconColor(step: MobileBuildStep): string {
      if (step.status === "completed") {
        if (step.conclusion === "success") return "success";
        if (step.conclusion === "skipped") return "grey";
        return "error";
      }
      if (step.status === "in_progress") return "primary";
      return "grey lighten-1";
    },
    stepDuration(step: MobileBuildStep): string {
      if (!step.startedAt) return "";
      const start = new Date(step.startedAt).getTime();
      const end =
        step.status === "completed" && step.completedAt
          ? new Date(step.completedAt).getTime()
          : step.status === "in_progress"
          ? this.now
          : 0;
      if (!end) return "";
      const seconds = Math.max(0, Math.floor((end - start) / 1000));
      if (seconds < 60) return `${seconds}s`;
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${m}m ${s.toString().padStart(2, "0")}s`;
    },
    formatTime(iso: string): string {
      try {
        return new Date(iso).toLocaleString();
      } catch {
        return iso;
      }
    }
  }
});
